<template>
  <div
    class="iorder-item"
    :class="{
      'iorder-item--featured': isFeaturedItem,
      'iorder-item--featured-image': isFeaturedItem && product.imageUrl,
      'iorder-item--list': listView,
    }"
  >
    <base-image
      v-if="isFeaturedItem && product.imageUrl"
      class="iorder-item__image"
      :desktop-url="product.imageUrl"
      :desktop-alt="product.imageAltText"
      :custom-params="{ desktop: 500, tablet: 400, mobile: 715, mobileS: 430 }"
    />
    <span v-if="isNew" class="iorder-item__new">
      <base-badge variant="pill">{{ t('iorder-new') }}</base-badge>
    </span>
    <div class="iorder-item__content">
      <div v-if="product.name || description || portions.length" class="iorder-item__texts">
        <h4 class="iorder-item__title">{{ product.name }}</h4>
        <div v-if="portions.length" class="iorder-item__portions">
          <template v-if="portions.length === 1">
            <span v-if="portions[0].price" class="iorder-item__portion-price">
              {{ portions[0].formatedPrice }}
            </span>
          </template>
          <template v-for="portion in portions" v-else :key="portion.name">
            <div v-if="portion.price" class="iorder-item__portion">
              <div class="sr-only">{{ `${portion.name} - ${portion.formatedPrice}` }}</div>
              <span aria-hidden="true" class="iorder-item__portion-name">{{ portion.name }}</span>
              <span aria-hidden="true" class="iorder-item__portion-price">
                {{ portion.formatedPrice }}
              </span>
            </div>
          </template>
        </div>
        <p v-if="description" class="iorder-item__desc">
          {{ truncatedDescription }}
        </p>
      </div>
      <div class="iorder-item__params">
        <div v-if="tags.length" class="iorder-item__tags">
          <template v-for="{ name, allergenLink, green, icon, label } in tags" :key="name">
            <base-badge v-if="allergenLink">
              <base-link
                class="no-underline"
                :field="{ value: allergenLink }"
                @click="onAllergenLinkClick(allergenLink)"
              />
            </base-badge>
            <base-badge v-else :variant="green ? 'filled' : 'outline'" :icon="icon">
              {{ label || name }}
            </base-badge>
          </template>
        </div>
        <button
          v-if="showViewDetails"
          class="link iorder-item__view-details"
          @click="isDetailsModalOpen = true"
        >
          {{ t('iorder-view-details') }}
        </button>
      </div>
    </div>
    <menu-item-details-modal
      v-if="isDetailsModalOpen"
      :product="product"
      :tags="tags"
      @close="isDetailsModalOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import type { MenuItemProps, TagData } from './types';
import type { MenuProduct, MenuPortion, MenuProductOption } from '@/types/menu';
import { ref, computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { twoDecimalsFilter } from '../../../utils/filters';
import { tagsData } from './tagsData.js';
import BaseImage from '../../2_elements/BaseImage/BaseImage.vue';
import BaseLink from '../../2_elements/BaseLink/BaseLink.vue';
import BaseBadge from '../../2_elements/BaseBadge/BaseBadge.vue';
import MenuItemDetailsModal from '../MenuItemDetailsModal/MenuItemDetailsModal.vue';
import { useMenuInteractionTracking } from '../../../data-layer/hooks/useMenuInteractionTracking.js';

const { trackMenuInteraction } = useMenuInteractionTracking();

const { t } = useI18n();

const allergenData = inject('allergenData', ref<{ allergensLink?: { href?: string } }>({}));

const props = withDefaults(defineProps<MenuItemProps>(), {
  product: () => ({}) as MenuProduct,
  showCalories: false,
  listView: false,
});

const isDetailsModalOpen = ref(false);

const DESCRIPTION_MAX_LENGTH = 108;

const isFeaturedItem = computed(() => props.product?.keywords?.includes('FeaturedDish'));
const isNew = computed(() => props.product?.keywords?.includes('New'));

const removeCalories = (str: string) =>
  str ? str.replaceAll(/\s*\([^)]*kcal[^)]*\)/gi, '').trim() : '';

const description = computed(() => removeCalories(props.product?.description || ''));

const isTruncated = computed(() => description.value.length > DESCRIPTION_MAX_LENGTH);

const truncatedDescription = computed(() => {
  if (!isTruncated.value) return description.value;

  let truncated = description.value.slice(0, DESCRIPTION_MAX_LENGTH);
  const lastSpace = truncated.lastIndexOf(' ');

  truncated = lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated;
  truncated = truncated.replace(/[\s.,!?:;-]+$/, '');

  return truncated + '…';
});

const getPrompts = (product: MenuProduct) =>
  (product?.options || []).filter((item): item is MenuProductOption => item?.type === 'PROMPT');

const hasPrompts = computed(() => getPrompts(props.product).length > 0);

const showViewDetails = computed(
  () => hasPrompts.value || !!props.product?.imageUrl || isTruncated.value
);

const portions = computed(() =>
  props.product?.portions
    ? props.product.portions
        .map((portion: MenuPortion) => ({
          ...portion,
          formatedPrice: `£${twoDecimalsFilter(portion.price)}`,
        }))
        .sort((a: MenuPortion, b: MenuPortion) => a.price - b.price)
    : []
);

const tags = computed(() => {
  const tagsArr: TagData[] = [];

  if (allergenData?.value?.allergensLink?.href) {
    tagsArr.push({
      name: t('iorder-allergen-dietary-info'),
      icon: 'icon-Alert-info-triangle',
      green: false,
      allergenLink: {
        href: allergenData.value.allergensLink.href,
        text: t('iorder-allergen-info'),
        target: '_blank',
        dl_menuSection: 'allergens',
      },
    });
  }

  const calories = props.product?.portions?.[0]?.calories;
  if (calories && props.showCalories) tagsArr.push({ name: `${calories} kcal` });

  const keywords = props.product?.keywords
    ? (tagsData.filter((tag) => props.product.keywords.includes(tag.name)) as TagData[])
    : [];
  return [...tagsArr, ...keywords];
});

const onAllergenLinkClick = ({
  href,
  dl_menuSection = '',
}: {
  href: string;
  dl_menuSection?: string;
}) => {
  trackMenuInteraction(href, dl_menuSection, 'download');
};
</script>

<style lang="scss">
@import './scss/MenuItem.scss';
</style>
